/**
 * ETTS API client — same-origin fetch nad Spring Boot REST API.
 *
 * Auth: cookie `etts_access` (HttpOnly JWT nastavený form-loginem) se posílá
 * automaticky (`credentials: "include"`). API chain (`/api/v1/**`) má CSRF
 * vypnutý → business volání CSRF token nepotřebují.
 *
 * Jediné form endpointy jsou `/admin/login` a `/admin/logout` — běží na web
 * security chainu s CSRF ON, takže potřebují token z `window.__ETTS__.csrfToken`
 * (viz `LoginPage.tsx` / `AppShell.tsx`). Všechna data chodí přes API chain.
 */

import { csrfHeaders } from "./bootstrap";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Tělo 403, kterým backend odmítá zamčenou/inzerovanou funkci
 * (`FeatureAccessInterceptor`). Nese dost informací na to, aby SPA vykreslila
 * vysvětlení místo obecné chyby.
 */
export interface FeatureBlock {
  featureId: string;
  featureName?: string;
  visibility: "ADVERTISED" | "LOCKED";
  advertisingText?: string | null;
  plannedVersion?: string | null;
}

export class FeatureBlockedError extends ApiError {
  constructor(
    status: number,
    body: unknown,
    public readonly block: FeatureBlock,
  ) {
    super(status, body, `Feature blocked: ${block.featureId}`);
    this.name = "FeatureBlockedError";
  }
}

/** Rozpozná feature-block tělo v odpovědi; jiné 403 (role) vrací null. */
function toFeatureBlock(body: unknown): FeatureBlock | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (b.code !== "FEATURE_ADVERTISED" && b.code !== "FEATURE_LOCKED") return null;
  if (typeof b.feature !== "string") return null;
  return {
    featureId: b.feature,
    featureName: typeof b.featureName === "string" ? b.featureName : undefined,
    visibility: b.code === "FEATURE_LOCKED" ? "LOCKED" : "ADVERTISED",
    advertisingText: typeof b.advertisingText === "string" ? b.advertisingText : null,
    plannedVersion: typeof b.plannedVersion === "string" ? b.plannedVersion : null,
  };
}

type SessionExpiredHandler = (currentPath: string) => void;
let sessionExpiredHandler: SessionExpiredHandler | null = null;
export function setSessionExpiredHandler(fn: SessionExpiredHandler): void {
  sessionExpiredHandler = fn;
}

/** Vypršelá session mimo `apiFetch` (SSE stream čte fetch přímo). */
export function notifySessionExpired(): void {
  sessionExpiredHandler?.(window.location.pathname);
}

/** Aktuální locale pro Accept-Language (nastavuje i18n při změně). */
let currentLocale = "cs";
export function setApiLocale(locale: string): void {
  currentLocale = locale;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json().catch(() => null);
  return res.text().catch(() => null);
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  /** JSON tělo (serializuje se automaticky). */
  json?: unknown;
  /** Když true, 401 NEspustí session-expired redirect (auth-flow endpointy). */
  skipAuthRedirect?: boolean;
}

export async function apiFetch<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { json, skipAuthRedirect, headers, ...rest } = options;
  // CSRF potřebuje jen form login/logout na web chainu; /api/** i /admin/api/**
  // mají CSRF vypnutý (autentizace přes JWT cookie se SameSite=Strict).
  const needsCsrf = path === "/admin/login" || path === "/admin/logout";
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Accept-Language": currentLocale,
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(needsCsrf ? csrfHeaders() : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  });

  if (res.status === 401 && !skipAuthRedirect) {
    sessionExpiredHandler?.(window.location.pathname);
  }

  if (!res.ok) {
    // Tělo se musí načíst dřív, než se rozhodne o typu výjimky — podle `code`
    // se pozná feature-block od běžného 403 (nedostatečná role).
    const body = await parseBody(res);
    const block = res.status === 403 ? toFeatureBlock(body) : null;
    if (block) {
      throw new FeatureBlockedError(res.status, body, block);
    }
    throw new ApiError(res.status, body, `HTTP ${res.status} ${path}`);
  }
  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}
